<script lang="ts">
	import { onMount } from 'svelte';
	// import Seo from '$lib/components/common/SEO.svelte';
	import Editor from '$lib/components/editor/editor.svelte';
	import type { PostAttrs } from '$lib/posts/post.type';
	import {
		postStore,
		postOwner,
		voidCanEditPost,
		deletePost,
		addDigs,
		getDigsCount,
		setPostDigs
	} from '$lib/posts';
	import { goto } from '$app/navigation';
	import { browser } from '$app/environment';
	import { user, getAuthorUsername, getUsernameByRealname } from '$lib/user';
	import type { PageData } from './$types';
	import BlogsUnderPost from '$lib/components/blog/BlogsUnderPost.svelte';
	import Comments from '$lib/components/comments/Comments.svelte';
	import { addNotification } from '$lib/chat/chat.api';
	import { profileStore } from '$lib/user';
	import { handleAlert } from '$lib/components/alert';
	import PostHeader from '$lib/components/editor/PostHeader.svelte';
	import PostActions from '$lib/components/editor/PostActions.svelte';
	import { communityPostsStore } from '$lib/components/blog/performantCommunityPosts';
	import PostMetadata from '$lib/components/editor/PostMetadata.svelte';
	import { SecureSanitizer } from '$lib/utils/secureSanitizer';

	// import AboutBlurb from '$lib/components/common/AboutBlurb.svelte';
	// ** get base64 for SEO
	// import { getPostImage } from '$lib/posts';

	// export let image_url;
	// export let alt = '...';

	// ** get base64 for SEO
	// let src;
	// let base64Image = '';

	export let data: PageData;
	let post: PostAttrs = data.post;
	let validated: boolean = data.validated;
	let seo = (data as any).seo || {
		title: 'Bloom Network Post',
		description: 'A post from the Bloom Network community',
		image: '/img/bloomLogoExploded.png',
		url: '',
		author: 'Bloom Network',
		publishedTime: undefined,
		modifiedTime: undefined,
		type: 'article',
		siteName: 'Bloom Network',
		twitterHandle: '@ourbloomnetwork'
	};
	// export let slug;
	let replied_to_username: string;
	let post_slug = '';
	let loading = true;
	let digsLoaded = false;
	// let maxDigs = 33;
	let CLEAN_MESSAGE = 'dig';
	let created_at = new Date();
	let mentioned: [] = [];
	let channelName = '';
	let digsCount = 0;
	let id = $profileStore?.id;
	let username = $profileStore?.username;
	let buttonText = 'Make Claim';
	let deleteConfirm = false;
	let showBountyMessage = false;
	$postStore = post;

	// Update button text based on bounty_posted status
	// $: buttonText = $postStore.bounty_posted ? 'Edit Claim' : 'Claim Bounty';

	// $: postClaimInfo = {
	// 	[post.slug]: {
	// 		id: post.claim_id,
	// 		author: post.created_by,
	// 		contributors: post.contributor_members
	// 	}
	// };

	// Function to update the post content
	async function updatePostContent() {
		$postStore = post;
		await assignAuthorUsername();
		// Check if the user can edit the post - use profileStore.id for ownership check
		if ($profileStore?.id && browser) {
			// Use profileStore.id for post ownership check since that's what's stored in post.created_by
			await voidCanEditPost($postStore, $profileStore.id);
		} else {
			// If we don't have valid profile data, set postOwner to false
			postOwner.set(false);
		}
	}

	async function handleClick() {
		// Validate user is logged in
		if (!$profileStore?.id) {
			// User authentication required for this action
			handleAlert({
				type: 'default',
				text: 'You must be logged in to dig posts'
			});
			return;
		}

		// Validate post exists
		if (!$postStore?.id) {
			console.error('Post ID is undefined');
			return;
		}

		// console.log('Attempting to dig post:', {
		// 	postId: $postStore.id,
		// 	userId: $profileStore.id,
		// 	username: $profileStore.username
		// });

		try {
			const CLEAN_USERNAME = $profileStore.username
				? await SecureSanitizer.sanitizeText($profileStore.username)
				: '';
			const replied_to_username = $postStore.author
				? await getUsernameByRealname($postStore.author)
				: '';

			const success = await addDigs($postStore.id, $profileStore.id, 1);
			// console.log('Dig result:', success);

			if (success) {
				const newDigsCount = digsCount + 1;
				digsCount = newDigsCount;
				postStore.update((store) => ({ ...store, digs: newDigsCount }));
				await setPostDigs($postStore.id, newDigsCount);
				// console.log('Successfully updated digs count to:', newDigsCount);
			} else {
				console.error('Failed to update digs in database');
				handleAlert({
					type: 'error',
					text: 'Failed to save dig. Please try again.'
				});
				return;
			}

			// Only send notification if we have all required data
			if (CLEAN_USERNAME && $postStore.id && $profileStore.id) {
				await addNotification(
					CLEAN_MESSAGE,
					CLEAN_USERNAME,
					mentioned,
					replied_to_username,
					false,
					$postStore.id,
					$profileStore.id,
					created_at,
					'dig',
					'post',
					$postStore.slug || '',
					channelName || ''
				);
				// console.log('Notification sent for dig');
			}
		} catch (error) {
			console.error('Error in handleClick:', error);
			handleAlert({
				type: 'error',
				text: 'An error occurred while saving your dig. Please try again.'
			});
		}
	}

	async function handleClaimClick() {
		const slug = $postStore.slug;
		if (!$postStore.bounty_posted) {
			// Route to /bounty if no bounty is posted
			goto(`/bounty?report=${slug}&postId=${$postStore.id}`);
		} else {
			showBountyMessage = true;
		}
		// TODO: this needs a different approach if we're going to do this... either do a query for claimId to the contract, or pass the database information in as params
		// 	const queryParams = new URLSearchParams({
		// 		postId: $postStore.claim_id || '',
		// 		contributors: JSON.stringify($postStore.contributor_members)
		// 	});
		// 	goto(`/bounty/claim/${$postStore.claim_id}?${queryParams.toString()}`);
		// 	// }
		// }
	}

	const assignAuthorUsername = async () => {
		try {
			// Call getAuthorUsername with $postStore.created_by
			if ($postStore.created_by) {
				replied_to_username = await getAuthorUsername($postStore.created_by);
			} else {
				replied_to_username = '';
			}
			post_slug = $postStore.slug ?? '';
		} catch (error) {
			console.error('Error:', error);
			// Handle error if necessary
		}
	};

	// Reactively update the post content when the slug changes
	$: if (data.post && data.post.id !== post?.id) {
		post = data.post;
		validated = data.validated;
		seo = (data as any).seo || {
			title: 'Bloom Network Post',
			description: 'A post from the Bloom Network community',
			image: '/img/bloomLogoExploded.png',
			url: '',
			author: 'Bloom Network',
			publishedTime: undefined,
			modifiedTime: undefined,
			type: 'article',
			siteName: 'Bloom Network',
			twitterHandle: '@ourbloomnetwork'
		};
		updatePostContent();
	}

	// Reactively check edit permissions when profileStore is loaded
	$: if ($profileStore?.id && $postStore?.id && !loading) {
		// Only check permissions if we haven't already determined postOwner status
		// This prevents unnecessary API calls
		if (typeof $postOwner === 'undefined' || $postOwner === false) {
			updatePostContent();
		}
	}

	onMount(async () => {
		// $commentsStore = [];
		// Initialize the post content on component mount
		// Load settings
		loading = false;

		// Load digs, share, and comments only after settings are loaded
		if (!loading && $postStore.id) {
			digsCount = await getDigsCount($postStore.id);
			digsLoaded = true; // Mark digs as loaded
		}

		// Fallback: Check edit permissions after a short delay to ensure profileStore is loaded
		setTimeout(async () => {
			if ($postStore?.id && $profileStore?.id) {
				// console.log('Fallback: Checking edit permissions in onMount');
				await updatePostContent();
			}
		}, 1000);

		// Replace with the actual blob retrieval logic
		// const response = await fetch('image_url');
		// const blob = src;
		// console.log('blob', blob);
	});

	// afterUpdate(() => {
	// 	// Once the data has been updated, set loading to false
	// 	loading = false;
	// });

	// ** get base64 for SEO
	// function blobToBase64(blob) {
	// 	return new Promise((resolve, reject) => {
	// 		const reader = new FileReader();
	// 		reader.onerror = reject;
	// 		reader.onload = () => {
	// 			resolve(reader.result.split(',')[1]);
	// 		};
	// 		reader.readAsDataURL(blob);
	// 	});
	// }

	function handleDeleteClick() {
		if (!$user?.member) {
			handleAlert({
				type: 'default',
				text: 'You must be an active member to delete this post. Or, to request content deletion email community@bloomnetwork.earth'
			});
			goto('/auth');
			return;
		} else if ($user?.member) deleteConfirm = true;
	}

	const onDelete = async () => {
		if (!$user?.member) {
			await goto('/auth');
			return;
		}

		if ($postStore.id) {
			const success = await deletePost($postStore.id);
			if (success) {
				handleAlert({
					type: 'success',
					text: 'Post has been deleted successfully.'
				});
				communityPostsStore.reset();
				// Small delay to allow the alert to be seen before navigation
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await goto('/');
			} else {
				handleAlert({
					type: 'error',
					text: 'Failed to delete post. Please try again.'
				});
				deleteConfirm = false;
			}
		} else {
			handleAlert({
				type: 'error',
				text: 'Could not delete post: Post ID not found.'
			});
			deleteConfirm = false;
		}
	};

	const onEdit = () => {
		goto(`/edit/${$postStore.slug}`);
	};

	// const buttonClass =
	// 	'border border-gradientBlueMid rounded-sm px-2 py-2 3xl:py-4 text-center w-24 xl:w-28 text-xs md:text-sm xl:text-lg tracking-wider';
</script>

<svelte:head>
	<!-- Basic Meta Tags -->
	<title>{seo.title}</title>
	<meta name="description" content={seo.description} />
	<meta
		name="keywords"
		content="regeneration, climate, justice, media, social, community, ReFi, bloom network"
	/>
	<meta name="author" content={seo.author} />
	<meta name="robots" content="index, follow" />

	<!-- Canonical URL -->
	<link rel="canonical" href={seo.url} />

	<!-- Open Graph Meta Tags -->
	<meta property="og:title" content={seo.title} />
	<meta property="og:description" content={seo.description} />
	<meta property="og:image" content={seo.image} />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:alt" content={seo.title || 'Bloom Network Post'} />
	<meta property="og:url" content={seo.url} />
	<meta property="og:type" content="article" />
	<meta property="og:site_name" content={seo.siteName} />
	<meta property="og:locale" content="en_US" />

	<!-- Open Graph Article Specific Tags -->
	<meta property="article:author" content={seo.author} />
	{#if seo.publishedTime}
		<meta property="article:published_time" content={seo.publishedTime} />
	{/if}
	{#if seo.modifiedTime}
		<meta property="article:modified_time" content={seo.modifiedTime} />
	{/if}
	<meta property="article:section" content={seo.type} />
	<meta
		property="article:tag"
		content="regeneration, climate, justice, media, social, community, ReFi"
	/>

	<!-- Twitter Card Meta Tags -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={seo.title} />
	<meta name="twitter:description" content={seo.description} />
	<meta name="twitter:image" content={seo.image} />
	<meta name="twitter:creator" content={seo.twitterHandle} />
	<meta name="twitter:site" content={seo.twitterHandle} />

	<!-- Additional Meta Tags for Better SEO -->
	<meta name="viewport" content="width=device-width, initial-scale=1" />
	<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />

	<!-- Structured Data for Rich Snippets -->
	<script type="application/ld+json">
		{
			"@context": "https://schema.org",
			"@type": "Article",
			"headline": "{seo.title || 'Bloom Network Post'}",
			"description": "{seo.description || 'A post from the Bloom Network community'}",
			"image": "{seo.image || '/img/bloomLogoExploded.png'}",
			"author": {
				"@type": "Person",
				"name": "{seo.author || 'Bloom Network'}"
			},
			"publisher": {
				"@type": "Organization",
				"name": "Bloom Network",
				"logo": {
					"@type": "ImageObject",
					"url": "/img/bloomLogoExploded.png"
				}
			},
			"datePublished": "{seo.publishedTime || ''}",
			"dateModified": "{seo.modifiedTime || seo.publishedTime || ''}",
			"mainEntityOfPage": {
				"@type": "WebPage",
				"@id": "{seo.url || ''}"
			}
		}
	</script>
</svelte:head>

<!-- <svelte:head>
	/ SEO Page Title /
	<Seo title={$postStore.title} description={$postStore.description} />
	/ dont use this yet / image={`data:image/jpeg;base64,${base64Image}`}
</svelte:head> -->

<!-- <meta
		property="og:image"
		content={`data:image/jpeg;base64,${base64Image}`} -->

<!-- <img class="max-h-60 rounded-2xl mx-auto object-contain" {alt} {src} /> -->
{#key $postStore.id}
	<div
		class="bg-[url('/img/modalBg3.png')] bg-opacity-75 bg-cover font-mono text-white text-left -mt-32 pt-24 pb-5 lg:pb-10
 3xl:max-w-full overflow-x-hidden rounded-t-2xl"
	>
		<!-- bg-[url('/img/modalBgLightNew.png')] dark: -->
		<div class="px-0 sm:px-4 mx-auto mt-4 xl:w-11/12 2xl:w-5/6 3xl:w-3/4 4xl:w-2/3">
			{#if ($postStore.published && ($postStore.membersonly === false || ($user?.member && $postStore.membersonly === true))) || (!$postStore.published && $postOwner)}
				<div class=" md:px-10 font-sans text-white text-left pt-5 mt-5">
					<div class="bg-cosmicPurple800 px-4 md:px-5 lg:px-7 3xl:px-16 pb-5 rounded-t-2xl pt-5">
						{#if !$postStore.published && $postOwner}
							<p class="text-cosmicPurple100 italic text-sm">
								This post is a draft. Only you can currently see this post.
							</p>
						{/if}
						{#if $postStore.type == 'action' && validated}
							{#if $postOwner}
								<p class="text-cosmicPurple100 pb-2">
									✔️ This action has been validated and editing is disabled.
								</p>
							{:else}
								<p class="text-cosmicPurple100 pb-2">✔️ This action has been verified.</p>
							{/if}
						{/if}

						{#if $postStore.type === 'action' || ($postOwner && !validated)}
							<div class="flex flex-row justify-between items-center mb-2">
								{#if $postStore.type === 'action'}
									<div
										class="font-light font-sans lg:text-xl md:pt-2 text-cosmicPurple100 text-left"
									>
										REGENERATIVE ACTION
									</div>
								{/if}
								{#if $postOwner && !validated}
									<PostActions
										{deleteConfirm}
										{buttonText}
										showClaim={$postStore.type === 'action'}
										on:edit={onEdit}
										on:delete={onDelete}
										on:deleteConfirm={handleDeleteClick}
										on:claim={handleClaimClick}
									/>
								{/if}
							</div>
							{#if $postOwner && showBountyMessage && $postStore.bounty_posted}
								<p
									class="mt-4 text-sm text-cosmicPurple100 text-center max-w-[380px] border-2 border-cosmicMagenta400 rounded-lg p-2 bg-cosmicMagenta400 bg-opacity-50 mx-auto"
								>
									You have already made a bounty claim for this post. To edit it, go to your
									<a
										href="/member/{$profileStore.username}?view=claims"
										class="underline text-bloomBlueNormal text-sm"
									>
										claims
									</a>
									and edit it from there.
								</p>
							{/if}
						{/if}

						<PostHeader
							editableTitle={false}
							title={$postStore.title || ''}
							author={$postStore.author || ''}
							created_at={$postStore.created_at}
							{digsCount}
							digDisabled={false}
							shareSlug={$postStore.slug || ''}
							shareTitle={$postStore.title || ''}
							showDigs={true}
							showShare={true}
							on:dig={handleClick}
							location={$postStore.location}
						/>
						<PostMetadata post={$postStore} type={$postStore.type} view={true} />
					</div>
					<div class="w-full bg-cosmicPurplebg md:pb-10 rounded-b-2xl">
						<Editor
							delta={$postStore.contents ? $postStore.contents : ''}
							view={true}
							userSystemRole={data.userSystemRole}
						/>
					</div>
				</div>
			{/if}
		</div>
	</div>
{/key}

{#if $postStore.id && digsLoaded}
	<div class="bg-cosmicPurplebg px-3 md:px-10 lg:px-5 xl:px-10 py-5 pt-5 rounded-b-2xl">
		<Comments post_id={$postStore.id} {replied_to_username} {post_slug} {id} {username} />
	</div>
{/if}

{#if $postStore.id && !loading}
	<BlogsUnderPost />
{/if}
